/**
 * Cloud VMs in the console (ADR 0106 decisions 3, 3a, 5): what a VM's card says and offers, pure
 * so the words and the order are tested. The data is state.ext.cloud (module 'cloud' on the
 * control plane, packages/protocol/src/ext/cloud-vms.ts).
 */
import { AWS_REGIONS, AWS_VM_SIZES, awsDiskUsdPerMonth, awsHourlyUsd, cloudVmStatus } from '@orgus/protocol';
import type { CloudInstanceCandidate, CloudState, CloudVm, Machine } from '@orgus/protocol';
import type { RowStatusProps } from '@orgus/ui';
import { formatMoney } from '@orgus/ui';

/** The VM Orgus created for this machine, or null (a machine joined by pasted cloud-init has none). */
export function vmOfMachine(state: CloudState | null | undefined, machineId: string): CloudVm | null {
  return state?.vms.find((v) => v.machineId === machineId) ?? null;
}

/** VMs Orgus created whose machine has not joined yet: shown as their own card, Starting. */
export function vmsWithoutMachine(state: CloudState | null | undefined, machines: Record<string, Pick<Machine, 'id'>>): CloudVm[] {
  return (state?.vms ?? []).filter((v) => !v.machineId || !machines[v.machineId]);
}

/** The card's one status while the VM is between states or stopped; null: the machine's own status applies. */
export function vmRowStatus(vm: CloudVm, machineOnline: boolean): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> | null {
  const s = cloudVmStatus(vm, machineOnline);
  if (s === 'starting') return { tone: 'working', icon: 'working', label: 'Starting' };
  if (s === 'stopping') return { tone: 'working', icon: 'working', label: vm.op?.waitUntil ? 'Stopping after running work' : 'Stopping' };
  if (s === 'deleting') return { tone: 'working', icon: 'working', label: vm.op?.waitUntil ? 'Deleting after running work' : 'Deleting' };
  if (s === 'stopped') return { tone: 'neutral', icon: 'stopped', label: 'Stopped' };
  return null;
}

export type VmMenuKey = 'stop' | 'start' | 'restart' | 'keep' | 'delete';

export interface VmMenuItem {
  key: VmMenuKey;
  label: string;
  disabled?: boolean;
}

/**
 * The VM items of a machine's menu, above the common ones (ADR 0106 decision 3): Stop VM or
 * Start VM, Restart VM, Keep this VM (ephemeral only), Delete VM. While Orgus is changing the
 * VM only Delete stays open.
 */
export function vmMenuItems(vm: Pick<CloudVm, 'state' | 'op' | 'lifetime'>): VmMenuItem[] {
  const busy = !!vm.op || vm.state === 'pending' || vm.state === 'stopping' || vm.state === 'shutting-down';
  const deleting = vm.op?.kind === 'delete' || vm.state === 'shutting-down' || vm.state === 'terminated';
  const items: VmMenuItem[] = [];
  if (vm.state === 'stopped') items.push({ key: 'start', label: 'Start VM', disabled: busy });
  else items.push({ key: 'stop', label: 'Stop VM', disabled: busy });
  items.push({ key: 'restart', label: 'Restart VM', disabled: busy || vm.state !== 'running' });
  if (vm.lifetime === 'ephemeral') items.push({ key: 'keep', label: 'Keep this VM', disabled: deleting });
  items.push({ key: 'delete', label: 'Delete VM…', disabled: deleting });
  return items;
}

const SIZE_TEXT = (id: string) => {
  const s = AWS_VM_SIZES.find((x) => x.id === id);
  return s ? `${s.vcpus} vCPU, ${s.memoryGib} GB` : id;
};

/** "~$0.04 an hour", or null when the table has no price. */
export function hourlyText(usd: number | null): string | null {
  return usd === null ? null : `~${formatMoney(usd)} an hour`;
}

/** "~$1.60 a month for its disk": what a stopped VM still pays (its EBS disk), or null. */
export function stoppedText(region: string): string | null {
  const usd = awsDiskUsdPerMonth(region);
  return usd === null ? null : `~${formatMoney(usd)} a month for its disk`;
}

/** The VM's cost now: the hourly estimate while it runs or starts, the disk while it is stopped. */
export function vmCostText(vm: Pick<CloudVm, 'state' | 'hourlyUsd' | 'region'>): string | null {
  if (vm.state === 'running' || vm.state === 'pending') return hourlyText(vm.hourlyUsd);
  if (vm.state === 'stopped' || vm.state === 'stopping') return stoppedText(vm.region);
  return null;
}

/** Size choices with what each is for, whether the AWS Free Tier plan runs it, and its hourly estimate in the region. */
export function sizeOptions(region: string): { value: string; label: string }[] {
  return AWS_VM_SIZES.map((s) => {
    const price = hourlyText(awsHourlyUsd(s.id, region));
    return { value: s.id, label: [`${s.id}, ${SIZE_TEXT(s.id)}`, s.freeTier ? 'Free Tier' : null, price].filter(Boolean).join(', ') };
  });
}

export const REGION_OPTIONS = AWS_REGIONS.map((r) => ({ value: r.id, label: `${r.label}, ${r.id}` }));

/** "AWS, us-east-1, t3.medium (2 vCPU, 4 GB)": the VM in one line for the drawer. */
export function vmFacts(vm: Pick<CloudVm, 'region' | 'size'>): string {
  return `AWS, ${vm.region}, ${vm.size} (${SIZE_TEXT(vm.size)})`;
}

/** What Delete loses, for its confirmation. */
export function deleteVmText(vm: Pick<CloudVm, 'name' | 'instanceId'>, running: number): string {
  const work = running > 0 ? ` New work stops now, and its ${running === 1 ? 'running item finishes' : `${running} running items finish`} first, for up to 10 minutes.` : '';
  return `AWS terminates ${vm.instanceId} and its disk is lost: files on the VM that were not pushed are gone. It leaves Orgus.${work}`;
}

/**
 * What the Machines page shows above the cards (ADR 0106 amendment 24 Oct 2026): nothing, the
 * calm Connect AWS entry for an owner or admin, or (connected) the instances they may adopt.
 */
export type CloudBandMode = 'hidden' | 'connect' | 'adopt';

export function cloudBandMode(cloud: { phase: string; state: CloudState | null; aws: { state: string } | null; canConnect: boolean }): CloudBandMode {
  if (cloud.phase !== 'ready' || !cloud.state?.provisioner.ready || !cloud.canConnect) return 'hidden';
  return cloud.aws?.state === 'connected' ? 'adopt' : 'connect';
}

/** "aws-claude-1, t3.medium, joined as ip-172-31-0-1": one candidate in a line. */
export function candidateFacts(c: Pick<CloudInstanceCandidate, 'size' | 'machineName'>): string {
  return [c.size, c.machineName ? `joined as ${c.machineName}` : 'not joined yet'].join(', ');
}

/** The title of the adopt band: "5 VMs in AWS run Orgus". */
export function adoptTitle(n: number): string {
  return `${n} ${n === 1 ? 'VM' : 'VMs'} in AWS ${n === 1 ? 'runs' : 'run'} Orgus`;
}

/** Choices for a permanent VM's Stop when idle rule (minutes, '' for never). */
export const IDLE_STOP_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '30', label: 'After 30 minutes idle' },
  { value: '60', label: 'After 1 hour idle' },
  { value: '120', label: 'After 2 hours idle' },
  { value: '480', label: 'After 8 hours idle' },
];
